<template>
    <ConfigurationModal
        :is-open="isOpen"
        :configuration-tip-link="$t('funnel.link.tipLink')"
        :configuration-tip-text="$t('funnel.link.tipText')"
        :title="$t('funnel.link.title.plural')"
        :funnel-id="funnelId"
        class="campaign-link-modal"
        @close-configuration-modal="closeCampaignLinkModal"
    >
        <template #configurationParameters>
            <div
                v-for="(campaignLink, i) in funnelCampaignLinks"
                :key="i"
                class="campaign-link-container"
                data-qa="campaign-link-container"
            >
                <div class="campaign-link-wrapper">
                    <ds-input-field
                        v-model="campaignLink.name"
                        :data-qa="`name_${campaignLink.id}`"
                        :label="$t('funnel.link.name')"
                        class="link-name"
                        required
                        :submitted="campaignLink.isSubmitted"
                        @input="handleInput(i)"
                    >
                        <template #error>
                            {{ $t('funnel.link.errRequired') }}
                        </template>
                    </ds-input-field>

                    <ds-input-field
                        v-model="campaignLink.url"
                        :data-qa="`url_${campaignLink.id}`"
                        :label="$t('funnel.link.Url')"
                        required
                        :submitted="campaignLink.isSubmitted"
                        @input="handleInput(i)"
                    >
                        <template #error>
                            {{ $t('funnel.link.errRequired') }}
                        </template>
                    </ds-input-field>

                    <ds-tooltip position="top">
                        <template #reference>
                            <ds-icon-button
                                class="edit-field"
                                aria-label="edit an item"
                                :data-qa="`save_${campaignLink.id}`"
                                :name="campaignLink.isEditing ? 'save' : 'check'"
                                :disabled="disableSave"
                                @click="saveCampaignLink(i)"
                            />
                        </template>
                        <template #default>
                            <div> {{ $t('funnel.link.save') }}</div>
                        </template>
                    </ds-tooltip>

                    <ds-tooltip position="top">
                        <template #reference>
                            <ds-icon-button
                                class="delete-campaign-link"
                                aria-label="delete an item"
                                :data-qa="`delete_${campaignLink.id}`"
                                name="trash-2"
                                @click="deleteCampaignLink(i,campaignLink.id)"
                            />
                        </template>
                        <template #default>
                            <div> {{ $t('funnel.link.delete') }}</div>
                        </template>
                    </ds-tooltip>
                </div>
                <ds-text-area-field
                    v-model="campaignLink.description"
                    :data-qa="`desc_${campaignLink.id}`"
                    :label="$t('funnel.link.description')"
                    class="link-description"
                    @input="handleInput(i)"
                />
            </div>
            <div v-if="isUnableToDelete" class="error-alert-container">
                <ds-inline-alert
                    type="critical"
                    leading-icon="alert-circle"
                    @close="closeAlertError"
                >
                    {{ $t('funnel.link.inUse') }} {{ marketingEmailLabels }}
                </ds-inline-alert>
            </div>

            <span
                class="add-campaign-link"
                data-qa="add-campaign-link"
                @click="addCampaignLink"
            >
                <ds-icon name="add" />
                <span>{{ $t('funnel.link.addLink') }}</span>
            </span>
        </template>
    </ConfigurationModal>
</template>

<script>
import ConfigurationModal from '../ConfigurationModal.vue';
import {
    loadLinks,
    saveLink,
    deleteLink,
    checkMarketingEmailIdsUsingLinks,
} from './links.api';
import { executeOnAllMatchingEmails } from '../../utils/cell/automation-mapper-utils';

export default {
    components: {
        ConfigurationModal,
    },

    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        funnelId: {
            type: Number,
            default: 0,
        },
        draftJson: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['close-campaign-links', 'mark-dirty'],

    data() {
        return {
            funnelCampaignLinks: [],
            isUnableToDelete: false,
            disableSave: false,
            marketingEmailLabels: [],
        };
    },

    async mounted() {
        await this.loadingCampaignLinks();
    },

    methods: {
        async loadingCampaignLinks () {
            try {
                this.funnelCampaignLinks = await loadLinks(this.funnelId);

                const campaignLink = this.funnelCampaignLinks.map((link) => ({
                    ...link,
                    isEditing: true,
                    isSubmitted: false,
                }));

                this.funnelCampaignLinks = campaignLink;
            } catch (error) {
                this.$error({ message: this.$t('funnel.link.loading.error') });
            }
        },

        closeCampaignLinkModal() {
            this.isUnableToDelete = false;
            this.$emit('close-campaign-links');
        },

        async saveCampaignLink(index) {
            const campaignLink = this.funnelCampaignLinks[index];

            this.isUnableToDelete = false;

            if (campaignLink.name && campaignLink.url && this.allowedUrl(campaignLink.url)) {
                const payload = {
                    name: campaignLink.name,
                    url: campaignLink.url,
                    description: campaignLink.description,
                };

                try {
                    const campaignLinkId = await saveLink(this.funnelId, campaignLink.id, payload);

                    campaignLink.id = campaignLinkId;

                    const marketingEmailIds = await this.checkMarketingEmailIdsUsingLinks(campaignLinkId);

                    executeOnAllMatchingEmails(marketingEmailIds, this.draftJson, () => {
                        this.markDirty();
                    });
                    campaignLink.isEditing = false;
                } catch (error) {
                    this.$error({ message: this.$t('funnel.link.save.failure') });
                }
            } else {
                campaignLink.isSubmitted = true;
                this.disableSave = true;

                if (campaignLink.url && campaignLink.url.trim() && !this.allowedUrl(campaignLink.url)) {
                    this.$error({ message: this.$t('funnel.link.unsupported.link') });
                }
            }
        },

        async deleteCampaignLink(index, campaignLinkId) {
            if (campaignLinkId) {
                const marketingEmailIds = await this.checkMarketingEmailIdsUsingLinks(campaignLinkId);

                try {
                    if (marketingEmailIds.length === 0) {
                        await deleteLink(campaignLinkId);
                        await this.loadingCampaignLinks();
                    } else {
                        const marketingEmailLabelsArray = [];

                        executeOnAllMatchingEmails(marketingEmailIds, this.draftJson, (cell) => {
                            marketingEmailLabelsArray.push(cell);
                        });

                        this.marketingEmailLabels = marketingEmailLabelsArray.join(', ');
                        this.isUnableToDelete = true;
                    }
                } catch (error) {
                    this.$error({ message: this.$t('funnel.link.delete.failure') });
                }
            } else {
                this.funnelCampaignLinks.splice(index, 1);
            }
        },

        async checkMarketingEmailIdsUsingLinks(campaignLinkId) {
            try {
                return await checkMarketingEmailIdsUsingLinks(campaignLinkId);
            } catch (error) {
                this.$error({ message: this.$t('funnel.link.check.email.failure') });

                return false;
            }
        },

        addCampaignLink() {
            const newCampaignLink = {
                id: 0,
                label: '',
                value: '',
                description: '',
                isEditing: true,
                isSubmitted: false,
            };

            this.disableSave = false;
            this.isUnableToDelete = false;
            this.funnelCampaignLinks = [...this.funnelCampaignLinks, newCampaignLink];
        },

        closeAlertError() {
            this.isUnableToDelete = false;
        },

        handleInput(index) {
            this.disableSave = false;
            this.funnelCampaignLinks[index].isEditing = true;
        },

        markDirty() {
            this.$emit('mark-dirty');
        },

        allowedUrl(url) {
            const BLOCKED_SCHEME = /^(tel?|mail(to)?):/i;
            const BARE_PHONE = /^\+?[\d\s\-().]+$/;
            const BARE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

            const trimmed = (url || '').trim();

            if (!trimmed) return false;

            return !BLOCKED_SCHEME.test(trimmed)
                && !BARE_PHONE.test(trimmed) && !BARE_EMAIL.test(trimmed);
        },
    },
};
</script>

<style lang="scss">
div.campaign-link-modal  div.modal-body-inner,
div.campaign-link-modal  div.modal-body {
    padding: 0px;
}

div.campaign-link-wrapper section.tooltip {
    border-radius: 0.75rem;
    padding: 0.75rem;
}

div.campaign-link-wrapper section.tooltip div.arrow {
    filter: none;
    border: none;
}
</style>

<style lang="scss" scoped>
.campaign-link-container {
    display: inline-block;
    padding: 20px;
    border-bottom: 1px solid $color-gray-300;
}

.campaign-link-wrapper {
    display: inline-flex;
    margin-bottom: 15px;
    width: 100%;
}

.campaign-link-wrapper > div.input-field {
    margin: 5px;
}

.campaign-link-wrapper > div.link-name {
    width: 80%;
}

.campaign-link-container > div.link-description {
    margin-left: 5px;
    width: 85%;
    margin-bottom: 0px;
}

.edit-field, .delete-campaign-link {
    margin-top: 5px;
}

.add-campaign-link {
    align-items: center;
    color: #0042db;
    cursor: pointer;
    display: inline-flex;
    font-weight: 500;
    grid-gap: 0.5rem;
    width: 35%;
    padding: 20px;
}

.error-alert-container {
    padding: 20px;
}

</style>
